// SpuitSamplePreference — スポイト (eyedropper) のサンプル元モードを per-user で
// localStorage に永続化する infrastructure 層ユーティリティ.
//
// 背景 (docs/_wip/spuit-sample-modes-2026-06-22.md §4、consult 2026-06-22):
//   - レイヤー3枚実装後、スポイトのサンプル元を **3 モード切替** にする要望。
//     A 表示色 (全可視+bg、既定) / B レイヤーのみ (bg無視・α0で消しゴム・半透明は
//     RGB のみ) / C レイヤー+背景。
//   - スポイトは完全クライアントローカル (protocol 影響ゼロ、`protocol-events.md:499`
//     確定済) なので **per-user localStorage**、サーバ・他ユーザー同期不要。
//   - 既定は **A (`'display'`)** = CSP/PS 標準「見たままの色を取得」(設計書 §2)。
//
// 設計 (brushCursorPreference.ts / stabilizerPreference.ts と同流儀):
//   - 値はモード文字列 (`'display' | 'layer' | 'layer-bg'`) で保存。parse 失敗 /
//     不在 / storage 不可は **既定 (= A `'display'`)** に倒し、例外を throw しない。
//   - StorageLike 抽象 + sentinel probe で Safari private browsing / Quota を弾く。
//   - store factory は test で storage mock を注入可能。
//
// scope: ユーザー端末ローカル, サーバ同期なし。protocol 変更なし。Classic/Modern
//   共通 (= useRoomChrome がレイアウト非依存に持ち、DrawingCanvas へ流す)。
//
// 値の型 (`SpuitSampleMode`) は spuit ハンドラ (`presentation/layouts/classic/spuit.ts`)
// と engine (`@oekaki/drawing-core` の `SpuitMode`) で構造的に同型。enum 値が増減
// したら 3 箇所を揃える (= node env でテストするため engine への直接 import は避ける)。
//
// Tests: spuitSamplePreference.test.ts (storage-injection style)。

/** スポイトのサンプル元モード. 文字列で localStorage に保存される (A/B/C). */
export type SpuitSampleMode = 'display' | 'layer' | 'layer-bg';

/** localStorage キー: スポイトのサンプル元モード. */
export const SPUIT_SAMPLE_MODE_KEY = 'oekaki:spuit-sample-mode';

/** 既定値 (= A `'display'`、設計書 §2「表示色を取得 (全て合成)」). */
export const DEFAULT_SPUIT_SAMPLE_MODE: SpuitSampleMode = 'display';

/**
 * 文字列が有効な SpuitSampleMode か判定する type guard. unknown / 不正値の
 * defensive narrowing 用.
 */
export const isSpuitSampleMode = (v: unknown): v is SpuitSampleMode =>
  v === 'display' || v === 'layer' || v === 'layer-bg';

/**
 * Minimal Storage shape this module uses (brushCursorPreference.ts と同流儀).
 * `globalThis.localStorage` matches; tests pass a mock with the same surface.
 */
export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

export interface SpuitSamplePreferenceStore {
  /** サンプル元モードを読み出し. 不在 / 不正 / storage 不可 → 既定 (= A). */
  get(): SpuitSampleMode;
  /** サンプル元モードを保存. storage 不可なら no-op (例外を throw しない). */
  set(mode: SpuitSampleMode): void;
  /** key を削除 (= 既定に戻す). テスト / 設定リセット用. storage 不可なら no-op. */
  clear(): void;
}

/**
 * `globalThis.localStorage` をプローブして使用可能なら返す, さもなくば `null`.
 * brushCursorPreference.ts の probe を流用 (sentinel write + readback で Safari
 * private browsing / QuotaExceeded を弾く).
 */
const probeBrowserStorage = (): StorageLike | null => {
  try {
    const ls = globalThis.localStorage;
    if (!ls) return null;
    const sentinel = 'oekaki:spuit-sample-mode__probe__';
    ls.setItem(sentinel, '1');
    const got = ls.getItem(sentinel);
    ls.removeItem(sentinel);
    if (got !== '1') return null;
    return ls;
  } catch {
    return null;
  }
};

export interface CreateSpuitSamplePreferenceStoreOptions {
  /**
   * Override the underlying storage. Default: probe `globalThis.localStorage`,
   * fall through to no-op if unavailable. Pass `null` to **force** the no-op
   * path (= getter always returns the default, setter silently drops).
   *
   * テストでは小さな `Map` ベースの fake を渡す.
   */
  storage?: StorageLike | null;
}

const readSpuitSampleMode = (storage: StorageLike): SpuitSampleMode => {
  try {
    const raw = storage.getItem(SPUIT_SAMPLE_MODE_KEY);
    return isSpuitSampleMode(raw) ? raw : DEFAULT_SPUIT_SAMPLE_MODE;
  } catch {
    return DEFAULT_SPUIT_SAMPLE_MODE;
  }
};

const writeSpuitSampleMode = (storage: StorageLike, mode: SpuitSampleMode): void => {
  try {
    storage.setItem(SPUIT_SAMPLE_MODE_KEY, mode);
  } catch {
    // QuotaExceeded / SecurityError 等 — 永続化できないだけで動作は継続させる.
  }
};

/**
 * `SpuitSamplePreferenceStore` のインスタンスを構築. storage が unavailable な
 * 環境 (SSR / Safari private browsing 厳格モード) でも getter は既定を返すだけで、
 * setter は no-op (= 例外で UI が壊れることはない).
 */
export const createSpuitSamplePreferenceStore = (
  opts: CreateSpuitSamplePreferenceStoreOptions = {},
): SpuitSamplePreferenceStore => {
  const explicit = opts.storage;
  const storage = explicit === null ? null : (explicit ?? probeBrowserStorage());

  return {
    get() {
      if (storage === null) return DEFAULT_SPUIT_SAMPLE_MODE;
      return readSpuitSampleMode(storage);
    },
    set(mode) {
      if (storage === null) return;
      writeSpuitSampleMode(storage, mode);
    },
    clear() {
      if (storage === null) return;
      try {
        storage.removeItem(SPUIT_SAMPLE_MODE_KEY);
      } catch {
        // ignore
      }
    },
  };
};
